import React, { useMemo } from 'react'
import cx from 'classnames'
import { isArray } from 'lodash'
import { arrayOf, bool, func, number, oneOf, string } from 'prop-types'

import { FILE_ACCEPT_TYPES } from '../../constants/file.js'
import { fileSelectValidation } from '../../prop-types'
import { getDefaultFileSelectValidations, getFileItemsErrors } from '../../util/file'

import styles from './file-select.module.css'

const propTypes = {
    accept: string,
    acceptType: oneOf(Object.values(FILE_ACCEPT_TYPES)),
    additionalValidations: arrayOf(fileSelectValidation),
    className: string,
    disabled: bool,
    multiple: bool,
    onChange: func.isRequired,
    onInvalidFile: func,
    maxSize: number
}

const defaultProps = {
    acceptType: FILE_ACCEPT_TYPES.MIME_TYPES,
    additionalValidations: [],
    className: '',
    disabled: false,
    multiple: false,
    onInvalidFile: Function.prototype
}

/**
 *
 * @param props
 * @property {string} props.accept - The mime type expression of accepted files (f.e. "image/*")
 * @property {FilesValidation[]} props.additionalValidations - An array of validations to be used in addition to the standard set
 * @property {boolean} props.disabled - Flag to disable the file selector
 * @property {number} props.maxSize - The maximum size of accepted files in bytes
 * @property {boolean} props.multiple - Flag if it is possible to select multiple files at once
 * @property {function} props.onChange - Function to be called if the value of the current selected files changed
 * @property {function} props.onInvalidFile - Function to be called if file/s is/are rejected
 */
function FileSelect({
    accept,
    acceptType,
    additionalValidations,
    children,
    className,
    disabled,
    maxSize,
    multiple,
    onChange,
    onInvalidFile
}) {
    const input = React.createRef()
    const validations = useMemo(() => {
        const fullValidationsArray = getDefaultFileSelectValidations(multiple, maxSize, accept, acceptType)
        if (isArray(additionalValidations)) {
            fullValidationsArray.push(...additionalValidations)
        }

        return fullValidationsArray
    }, [multiple, maxSize, accept, acceptType, additionalValidations])

    const onInputChange = fileList => {
        const errors = getFileItemsErrors(fileList, validations)
        if (errors.length === 0) {
            onChange(multiple ? fileList : fileList[0])
        } else {
            onInvalidFile(errors, fileList)
        }
        if (input.current) {
            input.current.value = null
        }
    }

    return (
        <div className={cx(className, 't3sel-file-select')}>
            <input
                ref={input}
                {...{ accept, multiple }}
                type="file"
                onChange={({ target: { files } }) => onInputChange(files)}
                className={cx(styles['input'], 't3sel-file-select-input')}
            />
            <div onClick={() => !disabled && input.current.click()} className="t3sel-file-select-wrapper">
                {children}
            </div>
        </div>
    )
}

FileSelect.propTypes = propTypes
FileSelect.defaultProps = defaultProps

export { FileSelect }
